import React, { useState } from 'react';
import { func, object, number, array } from 'prop-types';
import { sortableContainer, SortableElement, sortableHandle } from 'react-sortable-hoc';
import { map, get, find, assign } from 'lodash';
import { Switch } from 'antd';
import { Row, Divider, Text, InputItemList, FormInput, Icon } from '../../common';
import { arrayMove } from '../../../utils/misc';

const SortableContainer = sortableContainer(({ children }) => {
    return <div>{children}</div>;
});

const ComponentRenderer = ({ modifySetting, setting, children }) => {
    const value = get(setting, 'value', false);
    const type = typeof value;
    const key = get(setting, 'key');

    const onChangeInput = (value) => {
        const formatValue = value => type === 'number' ? parseInt(value) : value;
        modifySetting(key, formatValue(get(value, 'target.value', 0)));
    };

    switch (type) {
    case 'object':
        return (
            <Row justify='space-between' align='center'>
                {children}
                <Row space>
                    <InputItemList
                        placeholder={get(setting, 'placeholder')}
                        data={value || []}
                        onChange={data => modifySetting(key, data)}
                        justify='flex-end'
                    />
                </Row>
            </Row>
        );
    case 'number':
        return (
            <Row justify='space-between' align='center'>
                {children}
                <FormInput
                    type='number'
                    onlyNumbers
                    onChange={onChangeInput}
                    value={value}
                    placeholder={get(setting, 'placeholder') || ''}
                    width='185px'
                    style={{ margin: 0 }}
                />
            </Row>
        );
    case 'string':
        return (
            <Row justify='space-between' align='center'>
                {children}
                <FormInput
                    onChange={onChangeInput}
                    width='300px'
                    value={value}
                    placeholder={get(setting, 'placeholder') || ''}
                    style={{ margin: 0 }}
                />
            </Row>
        );
    default:
        return (
            <Row justify='space-between' align='center'>
                {children}
                <Switch checked={value} onChange={value => {
                    modifySetting(key, value)
                }} />
            </Row>
        );
    }
};

ComponentRenderer.propTypes = {
    modifySetting: func,
    setting: object,
    children: object
};

const DragHandle = sortableHandle(({ isHoveringOption }) => (
    <Icon
        name='drag-vertical'
        color={isHoveringOption ? '#1fa02e' : 'white'}
        size={20}
    />
));

const SortableItem = SortableElement(({ value, sortStart, ...props }) => {
    const [isHoveringOption, setIsHoveringOption] = useState(false);

    return <>
        <Row
            align='center'
            mY={15} mR={20} mL={0}
            onMouseEnter={() => !sortStart && setIsHoveringOption(true)}
            onMouseLeave={() => !sortStart && setIsHoveringOption(false)}
        >
            <DragHandle isHoveringOption={isHoveringOption} />
            <ComponentRenderer {...props}>
                <Text text={value} />
            </ComponentRenderer>
        </Row>
        <Divider style={{ margin: '0px 20px', padding: 0 }} />
    </>;
});

const onSortEnd = ({ oldIndex, newIndex }, orderCategory, setGlobalStoreSettings, globalStoreSettings, setDisabledButton) => {
    setDisabledButton(false);
    const globalStoreSettingsUpdated = {};
    const category = find(globalStoreSettings, { order: orderCategory });
    const newOrderSettings = arrayMove(get(category, 'settings'), oldIndex, newIndex);

    const updateFieldOrder = () => {
        return map(newOrderSettings, (setting, index) => {
            return {
                ...setting,
                order: index + 1
            };
        });
    };

    map(globalStoreSettings, (globalStoreSetting, key) => {
        if (get(globalStoreSetting, 'order') === orderCategory) {
            assign(globalStoreSettingsUpdated, { [key]: { ...globalStoreSetting, settings: updateFieldOrder() } });
        } else {
            assign(globalStoreSettingsUpdated, { [key]: globalStoreSetting });
        }
    });

    setGlobalStoreSettings(globalStoreSettingsUpdated);
};

const Settings = ({
    globalStoreSettings,
    setGlobalStoreSettings,
    globalSettingsCurrent,
    modifySetting,
    storeSettings,
    orderCategory,
    setDisabledButton
}) => {
    const [sortStart, setSortStart] = useState(false);

    return (
        <SortableContainer
            onSortEnd={(indexes) => {
                setSortStart(false);
                onSortEnd(indexes, orderCategory, setGlobalStoreSettings, globalStoreSettings, setDisabledButton);
            }}
            onSortStart={() => setSortStart(true)}
            distance={10}
        >
            {map(globalSettingsCurrent, ({ value, key, placeholder }, index) => {
                const setting = find(storeSettings, { key: key }) || { key, value: false };

                return (<SortableItem
                    key={`item-${index}`}
                    index={index}
                    modifySetting={modifySetting}
                    value={value}
                    setting={{ ...setting, placeholder }}
                    sortStart={sortStart}
                />);
            })}
        </SortableContainer>
    );
};

Settings.propTypes = {
    globalStoreSettings: object,
    storeSettings: array,
    globalSettingsCurrent: array,
    orderCategory: number,
    modifySetting: func,
    setGlobalStoreSettings: func,
    setDisabledButton: func
};

export default Settings;
